import { useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import IconButton from '../../../components/IconButton';
import Icon from '../../../components/Icon';
import PaymentStatusHistoryItem from './PaymentStatusHistoryItem';
import type { AnyPaymentOut } from '../../../client/portal/types.gen';
import ListItem from '../../../components/ListItem';
import formatDateTime from '../../../utils/formatDateTime';
import Section from '../../../components/Section';
import useClickOutside from '../../../utils/useClickOutside';

type PaymentStatusHistoryProps = {
  hide: () => void;
  payment: AnyPaymentOut;
};

const PaymentStatusHistory = ({ payment, hide }: PaymentStatusHistoryProps) => {
  const section = useRef<HTMLDivElement>(null);

  useClickOutside(section, hide);

  const handleEscPress = useCallback(
    (event: KeyboardEvent) => {
      event.key === 'Escape' && hide();
    },
    [hide]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleEscPress, false);

    return () => document.removeEventListener('keydown', handleEscPress, false);
  }, [handleEscPress]);

  useEffect(() => {
    if (section.current) {
      section.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [section]);

  return (
    <HistorySection ref={section}>
      <Header>
        <h3>Payment history</h3>
        <IconButton options={{ size: 'medium' }} onClick={hide}>
          <Icon iconName='faTimes' />
        </IconButton>
      </Header>
      <HistoryWrapper>
        <ul>
          <PaymentStatusHistoryItem
            label='Requested'
            status='completed'
            at={payment.createdAt}
            by={payment.requestedBy}
          />
          {payment.signedAt && (
            <PaymentStatusHistoryItem
              label='Signed'
              status='completed'
              at={payment.signedAt}
              by={payment.signedBy}
            />
          )}
          {payment.sentToSebAt && (
            <PaymentStatusHistoryItem
              label='Sent to SEB'
              status='completed'
              at={payment.sentToSebAt}
            />
          )}
          {payment.acknowledgedBySebAt && (
            <PaymentStatusHistoryItem
              label='Acknowledged by SEB'
              status='completed'
              at={payment.acknowledgedBySebAt}
            />
          )}
          {payment.transactionDay && (
            <PaymentStatusHistoryItem
              label='Fulfilled'
              status='completed'
              at={payment.transactionDay}
            />
          )}
          {payment.rejectedBySebAt && (
            <PaymentStatusHistoryItem
              label='Rejected by SEB'
              status='rejected'
              at={payment.rejectedBySebAt}
              rejectionCode={
                payment.status.tag === 'PaymentSeb' &&
                payment.status.paymentSebStatus.tag === 'PaymentSebRejected'
                  ? payment.status.paymentSebStatus.sebInfoCode
                  : null
              }
              rejectionReason={
                payment.status.tag === 'PaymentSeb' &&
                payment.status.paymentSebStatus.tag === 'PaymentSebRejected'
                  ? payment.status.paymentSebStatus.sebInfoReason
                  : null
              }
            />
          )}

          {payment.rejectedBySebAt && (
            <ListItem theme={'red'}>
              <HistoryItemContentWrapper>
                <StatusWrapper status='rejected'>
                  <Icon iconName='faTimesCircle' />
                </StatusWrapper>
                <ContentWrapper>
                  <h4>Rejected</h4>
                  {formatDateTime(payment.rejectedBySebAt)}
                  {payment.status.tag === 'PaymentSeb' &&
                    payment.status.paymentSebStatus.tag ===
                      'PaymentSebRejected' && (
                      <>
                        <br />
                        {payment.status.paymentSebStatus.sebInfoReason}
                      </>
                    )}
                </ContentWrapper>
              </HistoryItemContentWrapper>
            </ListItem>
          )}
        </ul>
      </HistoryWrapper>
    </HistorySection>
  );
};

export default PaymentStatusHistory;

const HistorySection = styled(Section)`
  position: absolute;
  right: 0;
  top: 0.5rem;
  width: 25rem;
  background-color: #fff;
  box-shadow: rgba(0, 0, 0, 0.1) 0px 0.5rem 1rem;
  z-index: 10;
`;

const HistoryWrapper = styled.main`
  padding: 0.5rem;
  background-color: #fafafa;
  border-radius: 0.5rem;
  margin-left: 0;

  ul {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
`;

const HistoryItemContentWrapper = styled.div`
  display: flex;
`;

const StatusWrapper = styled.div<{
  status: 'completed' | 'rejected' | 'undone';
}>`
  display: flex;
  align-items: flex-start;
  background-color: ${({ status }) =>
    status === 'completed'
      ? '#3c763d'
      : status === 'rejected'
      ? '#721c24'
      : '#e6e6e6'};
  font-size: 1.125rem;
  color: ${({ status }) =>
    status === 'completed'
      ? '#dff0d8'
      : status === 'rejected'
      ? '#f8d7da'
      : '#fafafa'};
  margin-left: -0.5rem;
  margin-top: -0.5rem;
  margin-bottom: -0.5625rem;
  border-bottom-left-radius: 0.25rem;
  border-top-left-radius: 0.25rem;
  padding: 0.5rem 0.25rem;
  margin-right: 0.25rem;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #2b2b2b;
`;
